import {AhpErrorCodes} from '@microsoft/agent-host-protocol'
import {RpcError} from '@microsoft/agent-host-protocol/client'

import {formatError} from './format-error'
import {MissionControlError} from './mission-control/mission-control-environment-client'
import {asAhpVersionMismatch} from './protocol-support'
import {ProjectProvisioningError, type ProvisioningStatus} from './session-host/resolve-project'

// An `RpcError` is a deliberate host rejection of a well-formed request, so
// re-sending it on a fresh connection usually cannot change the outcome — it is
// held as a terminal `error` rather than driving the reconnect loop. `-32011`
// (`Conflict`) is the exception: it is an optimistic-concurrency failure against
// stale advertised state (e.g. authenticating a resource the daemon no longer
// advertises, see the note at `establish`), and the spec's prescribed recovery
// is to re-read and retry — which a reconnect does by re-fetching fresh
// snapshots. Add a code here only when the host can genuinely recover from it
// across a reconnect.
const RETRYABLE_RPC_ERROR_CODES: ReadonlySet<number> = new Set([AhpErrorCodes.Conflict])

// The MC fetcher throws its host app's fetch error (e.g. FetchCAPIError), not a
// MissionControlError, so read `status` structurally to hold any 5xx terminal.
function serverErrorStatus(err: unknown): number | undefined {
  if (err instanceof MissionControlError) return err.status
  if (err && typeof err === 'object' && typeof (err as {status?: unknown}).status === 'number') {
    return (err as {status: number}).status
  }
  return undefined
}

interface RelayTerminalStateDeps {
  logger: {error(message: string): void}
  onProvisioning?: (status: ProvisioningStatus) => void
  teardownClient: () => Promise<void>
  setStatus: (status: 'error', error: string) => void
  setClientEverConnected: (clientEverConnected: false) => void
}

export class RelayTerminalState {
  private readonly deps: RelayTerminalStateDeps

  /**
   * Set once a connection attempt fails with a permanent, non-retryable
   * condition (a deliberate host `RpcError`, a protocol-version mismatch, or a
   * terminal provisioning failure). The runtime then holds a terminal `error`
   * status and stops the reconnect loop until it is disposed.
   */
  private terminated = false

  constructor(deps: RelayTerminalStateDeps) {
    this.deps = deps
  }

  isTerminated(): boolean {
    return this.terminated
  }

  /**
   * Handle a failed (re)establish. Returns `true` when the failure is terminal
   * — surfaced as an `error` status with no reconnect — so callers skip their
   * retry path. Terminal conditions are a protocol-version mismatch, a terminal
   * provisioning failure, or any deliberate host `RpcError` outside the
   * retryable allowlist.
   */
  async handleTerminalEstablishError(err: unknown): Promise<boolean> {
    const message =
      this.detectVersionMismatch(err) ??
      this.detectProvisioningFailure(err) ??
      this.detectServerError(err) ??
      this.detectTerminalRpcError(err)
    if (message === null) return false
    // A partially-established connection may have already assigned an open
    // transport/client; release it before settling into the terminal `error`
    // state, since no later reconnect will run to clean it up.
    await this.deps.teardownClient()
    this.deps.setStatus('error', message)
    return true
  }

  private detectVersionMismatch(err: unknown): string | null {
    const mismatch = asAhpVersionMismatch(err)
    if (!mismatch) return null
    this.terminated = true
    this.deps.setClientEverConnected(false)
    this.deps.logger.error(`relay connect failed: ${mismatch.message}`)
    return mismatch.message
  }

  private detectProvisioningFailure(err: unknown): string | null {
    if (!(err instanceof ProjectProvisioningError)) return null
    // A `closed` reason means the root subscription dropped mid-provision, not a
    // genuine clone failure — let the normal reconnect re-drive provisioning.
    if (err.reason === 'closed') return null
    this.terminated = true
    this.deps.setClientEverConnected(false)
    this.deps.logger.error(`relay project provisioning failed: ${err.message}`)
    // Some failure paths (e.g. unsupported host) throw before emitting progress,
    // so surface the terminal status here rather than relying on the resolve step.
    this.deps.onProvisioning?.({phase: 'failed', error: err.message})
    return err.message
  }

  /**
   * A Mission Control 5xx is a server-side failure; reconnecting hammers the
   * same failing endpoint without changing the outcome, so hold it terminal.
   */
  private detectServerError(err: unknown): string | null {
    const status = serverErrorStatus(err)
    if (status === undefined || status < 500) return null
    this.terminated = true
    this.deps.setClientEverConnected(false)
    const msg = formatError(err)
    this.deps.logger.error(`relay connect failed (server error): ${msg}`)
    return msg
  }

  /**
   * A deliberate host rejection of a well-formed request. Retrying the identical
   * request on a fresh connection cannot change the outcome, so it is held as a
   * terminal `error` unless its code is in the retryable allowlist.
   */
  private detectTerminalRpcError(err: unknown): string | null {
    if (!(err instanceof RpcError) || RETRYABLE_RPC_ERROR_CODES.has(err.code)) return null
    this.terminated = true
    this.deps.setClientEverConnected(false)
    const msg = formatError(err)
    this.deps.logger.error(`relay connect failed (terminal): ${msg}`)
    return msg
  }
}
